<script lang="ts">
  import { LAYOUTS } from '@multiview/shared';
  import { engine, PIP_SIZES } from '../lib/player/engine.svelte.ts';
  import Tile from './Tile.svelte';

  interface Props { showStats: boolean }
  const { showStats }: Props = $props();

  const def = $derived(LAYOUTS[engine.effectiveLayout]);
  /** One big picture, shown edge to edge (see .edge). */
  const edge = $derived(def.slots === 1 || !!def.pip);

  let mosaicW = $state(0);
  let mosaicH = $state(0);

  /**
   * Every layout is drawn for a landscape screen. On a phone held upright the
   * same grid squeezes each picture into a tall sliver, so there the main
   * picture goes full width on top and the rest follow below it, each at 16:9.
   */
  const portrait = $derived(mosaicH > mosaicW && mosaicW > 0 && !def.pip && engine.slots.length > 1);
  const others = $derived(engine.slots.length - 1);

  const areas = $derived(portrait ? 'none' : def.areas.map((row) => `"${row}"`).join(' '));
  const columns = $derived(
    portrait
      ? others === 1 ? 'minmax(0, 1fr)' : 'repeat(2, minmax(0, 1fr))'
      : def.resizable ? `${engine.split}fr ${1 - engine.split}fr` : def.columns,
  );
  const rows = $derived(portrait ? 'none' : def.rows);
  const showDivider = $derived(!!def.resizable && engine.slots.length > 1 && !portrait);

  /* ----------------------------------------------------------------- divider */

  let mosaic: HTMLDivElement;
  let resizing = $state(false);

  /** Pointer x to the left column's share of the space between the paddings. */
  function splitAt(clientX: number): number {
    const rect = mosaic.getBoundingClientRect();
    const css = getComputedStyle(mosaic);
    const pad = parseFloat(css.paddingLeft) || 0;
    const gap = parseFloat(css.columnGap) || 0;
    const inner = rect.width - 2 * pad - gap;
    return inner > 0 ? (clientX - rect.left - pad - gap / 2) / inner : 0.5;
  }

  function onDividerDown(e: PointerEvent) {
    if (e.button !== 0) return;
    resizing = true;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    e.preventDefault();
  }

  function onDividerMove(e: PointerEvent) {
    if (resizing) engine.setSplit(splitAt(e.clientX), false);
  }

  function onDividerUp() {
    if (!resizing) return;
    resizing = false;
    engine.setSplit(engine.split);
  }

  // An audio-only angle still needs a running player, so it gets an off-screen
  // host instead of a tile in the grid.
  const audioOnly = $derived(engine.angles.filter((a) => a.role === 'audio'));

  function audioHost(node: HTMLElement, angleId: string) {
    engine.mount(angleId, node);
    return { destroy: () => engine.unmount(angleId) };
  }

  /* --------------------------------------------------------------------- pip */

  const pipShare = $derived(PIP_SIZES.find((s) => s.value === engine.pip.size)?.width ?? 0.2);
  const pipW = $derived(Math.max(160, mosaicW * pipShare));
  const pipH = $derived((pipW * 9) / 16);
  /** Inset from the screen edge, so the window never sits flush against it. */
  const MARGIN = 16;

  const pipLeft = $derived(MARGIN + engine.pip.x * Math.max(0, mosaicW - pipW - 2 * MARGIN));
  const pipTop = $derived(MARGIN + engine.pip.y * Math.max(0, mosaicH - pipH - 2 * MARGIN));

  const pipStyle = $derived(
    `position:absolute;z-index:6;left:${pipLeft}px;top:${pipTop}px;width:${pipW}px;height:${pipH}px;opacity:${engine.pip.opacity};`,
  );

  /**
   * Free dragging. Positions are stored as fractions of the free space rather
   * than pixels, so the window stays in its corner when the screen size changes.
   * A press that barely moves stays a click, which promotes the angle as usual.
   */
  let drag = $state<{ id: number; startX: number; startY: number; left: number; top: number; moved: boolean } | null>(null);
  let suppressClick = false;

  function onPipPointerDown(e: PointerEvent) {
    if (!def.pip || e.button !== 0) return;
    drag = { id: e.pointerId, startX: e.clientX, startY: e.clientY, left: pipLeft, top: pipTop, moved: false };
  }

  function onPipPointerMove(e: PointerEvent) {
    if (!drag || e.pointerId !== drag.id) return;
    const dx = e.clientX - drag.startX;
    const dy = e.clientY - drag.startY;
    if (!drag.moved) {
      if (Math.hypot(dx, dy) < 5) return;
      drag.moved = true;
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    }
    const freeX = Math.max(1, mosaicW - pipW - 2 * MARGIN);
    const freeY = Math.max(1, mosaicH - pipH - 2 * MARGIN);
    const clamp = (v: number) => Math.min(1, Math.max(0, v));
    // Not persisted on every move; only the final position is worth storing.
    engine.pip = {
      ...engine.pip,
      x: clamp((drag.left + dx - MARGIN) / freeX),
      y: clamp((drag.top + dy - MARGIN) / freeY),
    };
  }

  function onPipPointerUp(e: PointerEvent) {
    if (!drag || e.pointerId !== drag.id) return;
    if (drag.moved) {
      suppressClick = true;
      engine.setPip(engine.pip);
    }
    drag = null;
  }

  /** Swallows the click that ends a drag, before the tile turns it into a swap. */
  function onPipClickCapture(e: MouseEvent) {
    if (!suppressClick) return;
    suppressClick = false;
    e.stopPropagation();
    e.preventDefault();
  }
</script>

<div
  class="mosaic"
  bind:this={mosaic}
  bind:clientWidth={mosaicW}
  bind:clientHeight={mosaicH}
  class:resizing
  class:edge
  class:portrait
  style:grid-template-columns={columns}
  style:grid-template-rows={rows}
  style:grid-template-areas={areas}
>
  <!-- One wrapper per angle whatever the layout: swapping PiP and main only
       restyles it. A different element would remount the player and reload
       the stream. -->
  {#each [...engine.slots, ...engine.standbySlots] as tile (tile.angle.id)}
    {@const floating = !!def.pip && tile.slot !== 'm' && tile.slot !== 'standby'}
    {@const standby = tile.slot === 'standby'}
    <div
      class="cell"
      class:standby
      inert={standby}
      class:pip={floating}
      class:dragging={floating && drag?.moved}
      class:main-cell={tile.slot === 'm'}
      style={floating ? pipStyle : standby || portrait || (edge && tile.slot === 'm') ? '' : `grid-area:${tile.slot}`}
      role="presentation"
      onpointerdown={floating ? onPipPointerDown : undefined}
      onpointermove={floating ? onPipPointerMove : undefined}
      onpointerup={floating ? onPipPointerUp : undefined}
      onpointercancel={floating ? onPipPointerUp : undefined}
      onclickcapture={onPipClickCapture}
    >
      <Tile {tile} {showStats} />
    </div>
  {/each}

  {#if showDivider}
    <div
      class="divider"
      style:left={`calc(var(--safe-inset) + (100% - 2 * var(--safe-inset) - var(--gap)) * ${engine.split} + var(--gap) / 2)`}
      role="separator"
      aria-orientation="vertical"
      aria-valuenow={Math.round(engine.split * 100)}
      title="Ziehen zum Verschieben, Doppelklick für Mitte"
      onpointerdown={onDividerDown}
      onpointermove={onDividerMove}
      onpointerup={onDividerUp}
      onpointercancel={onDividerUp}
      ondblclick={() => engine.setSplit(0.5)}
    ></div>
  {/if}
</div>

{#each audioOnly as angle (angle.id)}
  <div class="audio-host" aria-hidden="true" use:audioHost={angle.id}></div>
{/each}

<style>
  .mosaic {
    position: relative;
    display: grid;
    gap: var(--gap);
    width: 100%;
    height: 100%;
    padding: var(--safe-inset);
  }

  .cell { display: flex; min-width: 0; min-height: 0; }
  /* Playing but unseen, out of the grid's flow. Kept in the viewport and full
     size: a browser may stop decoding a video it considers off-screen, and
     then the switch would wait for the picture after all. */
  .cell.standby { position: absolute; inset: 0; opacity: 0; pointer-events: none; z-index: 0; }

  /* One big picture: edge to edge, without the inset, frame or rounded corners
     meant to separate tiles. Absolute positioning measures from the padding
     box, so the PiP window keeps its distance from the edge -- but only
     without a grid-area: with one, the grid area becomes the containing block
     and the inset stays (measured on the TV: 920x500 at +20+20 of 960x540). */
  .edge .main-cell { position: absolute; inset: 0; }
  .edge .main-cell :global(.tile) { border: 0; border-radius: 0; }

  .mosaic.portrait { grid-auto-rows: auto; align-content: center; overflow-y: auto; }
  .portrait .cell { aspect-ratio: 16 / 9; }
  .portrait .main-cell { grid-column: 1 / -1; order: -1; }

  /* A wide, invisible grab area over the gap; the line only shows on hover. */
  .divider {
    position: absolute;
    top: var(--safe-inset);
    bottom: var(--safe-inset);
    width: 24px;
    transform: translateX(-50%);
    z-index: 7;
    cursor: col-resize;
    touch-action: none;
  }

  .divider::after {
    content: '';
    position: absolute;
    left: 50%;
    top: 0;
    bottom: 0;
    width: 3px;
    transform: translateX(-50%);
    border-radius: 2px;
    background: var(--border-strong);
    opacity: 0;
    transition: opacity 120ms linear;
  }

  .divider:hover::after,
  .resizing .divider::after { opacity: 1; }

  /* Keeps the cursor and stops text selection while the pointer is over a video. */
  .mosaic.resizing { cursor: col-resize; user-select: none; }

  .pip {
    border-radius: var(--radius);
    box-shadow: 0 6px 24px rgba(0, 0, 0, 0.6);
    cursor: grab;
    touch-action: none;
  }

  .pip.dragging { cursor: grabbing; }

  .audio-host {
    position: absolute;
    width: 1px; height: 1px;
    overflow: hidden;
    opacity: 0;
    pointer-events: none;
  }
</style>
